/**
 * useTenant
 * =========
 * React Query replacements for the tenant-related async thunks in commonSlice:
 *
 *   fetchTenants       -> useTenants()
 *   fetchActiveTenant  -> useActiveTenant()
 *   setCurrentTenant   -> useSetCurrentTenant()
 *   fetchLocations     -> useLocations()
 *
 * On tenant switch (`useSetCurrentTenant`), the caller is responsible for
 * resetting queries after the auth token has been refreshed — see
 * SwitchTenantModal for the canonical flow.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { queryKeys } from '~services/queryKeys';
import { handleQueryError } from '~services/queryUtils';
import api from '~services/api';
import type { Tenant, TenantsResultPage } from '~types/commonTypes';

// ---------------------------------------------------------------------------
// Query functions
// ---------------------------------------------------------------------------

async function fetchActiveTenantQuery(): Promise<Tenant> {
  const res = await api.http.get('/tenant');
  return res.data;
}

async function fetchTenantsQuery(): Promise<TenantsResultPage> {
  const res = await api.http.get('/tenants');
  return res.data;
}

async function fetchLocationsQuery(): Promise<string[]> {
  const res = await api.http.get('/tenant');
  return res.data.locations;
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/**
 * Fetches the currently active tenant.
 * Replaces `fetchActiveTenant` thunk + `state.common.activeTenant`.
 */
export function useActiveTenant() {
  return useQuery({
    queryKey: queryKeys.tenants.detail('active'),
    queryFn: fetchActiveTenantQuery,
    meta: { onError: handleQueryError },
  });
}

/**
 * Fetches the list of all available tenants.
 * Replaces `fetchTenants` thunk + `state.common.tenants`.
 */
export function useTenants() {
  return useQuery({
    queryKey: queryKeys.tenants.lists(),
    queryFn: fetchTenantsQuery,
    meta: { onError: handleQueryError },
  });
}

/**
 * Fetches tenant locations from the active tenant endpoint.
 * Replaces `fetchLocations` thunk + `state.common.locations`.
 */
export function useLocations() {
  return useQuery({
    queryKey: queryKeys.tenants.detail('locations'),
    queryFn: fetchLocationsQuery,
    meta: { onError: handleQueryError },
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

/**
 * Switches the active tenant via `PUT /tenant`.
 *
 * IMPORTANT: This mutation intentionally does NOT auto-reset queries in
 * onSuccess.  Callers must reset queries themselves *after* ensuring the
 * auth token has been refreshed (see SwitchTenantModal).  Resetting too
 * early causes every active query to refetch with the old token → 401s.
 *
 * Replaces the `setCurrentTenant` thunk + `resetStore` reducer action.
 */
export function useSetCurrentTenant() {
  return useMutation({
    mutationFn: async ({ tenantId }: { tenantId: string }) => {
      const res = await api.http.put('/tenant', { tenantId });
      return res.data;
    },
    onError: handleQueryError,
  });
}
